import type { ReactNode } from "react";

/**
 * Estado vazio, sem resultado, indisponível ou de erro (AGENTS §18).
 *
 * `variant` existe porque base vazia e filtro sem resultado são coisas
 * diferentes e o usuário precisa saber qual das duas está vendo. Estados vazios
 * NÃO podem conter controle falso: `action` só deve receber uma ação real.
 */
export interface EmptyStateProps {
  variant?: "empty" | "no-results" | "unavailable" | "error";
  title: string;
  description?: ReactNode;
  icon?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  variant = "empty",
  title,
  description,
  icon,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={className ? `cx-empty ${className}` : "cx-empty"}
      data-variant={variant}
      role={variant === "error" ? "alert" : undefined}
    >
      {icon && <span aria-hidden="true">{icon}</span>}
      <p className="cx-empty-title">{title}</p>
      {description && <p className="cx-empty-desc">{description}</p>}
      {action}
    </div>
  );
}

/** Bloco de carregamento que preserva a estrutura da superfície (§18). */
export function Skeleton({
  width = "100%",
  height = 16,
  radius,
  className,
}: {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
}) {
  return (
    <span
      className={className ? `cx-skeleton ${className}` : "cx-skeleton"}
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  );
}

/** Região de carregamento anunciada uma única vez a leitores de tela. */
export function LoadingBlock({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="status" aria-live="polite" aria-busy="true">
      <span className="cx-sr-only">{label}</span>
      {children}
    </div>
  );
}
